import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Paper,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";

import {
  GET_GOVERNING_BODIES_ADMIN_ALL,
  GET_GOVERNING_BODIES_ADMIN_SEARCH,
} from "../../constants/endpoints";
import { MIN_SEARCH_KEY_LENGTH, TIME_OUT_REQUEST } from "../../constants/data";
import { getList } from "../../dataProvider/API";
import { normalizeGoverningBodiesPayload } from "../../utils/governingBodyApi";

const useStyles = makeStyles({
  paper: {
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  listbox: {
    maxHeight: "320px",
    overflowY: "auto",
    paddingBottom: "56px",
    "& li": {
      alignItems: "flex-start",
      whiteSpace: "normal",
      lineHeight: 1.3,
      paddingTop: "8px",
      paddingBottom: "8px",
    },
    "& li:nth-child(even)": {
      backgroundColor: "#f7f9fc",
    },
  },
  optionLabel: {
    whiteSpace: "normal",
    wordBreak: "break-word",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    flexWrap: "nowrap",
    width: "100%",
    boxSizing: "border-box",
    gap: "8px",
    padding: "8px 12px",
    borderTop: "1px solid #e6e8eb",
    backgroundColor: "#fff",
    position: "sticky",
    bottom: 0,
    zIndex: 2,
  },
  chip: { maxWidth: "100%" },
});

const toNull = (value) => {
  if (value == null) return null;
  const str = String(value).trim();
  return str === "" ? null : str;
};

/** `{ id, abbreviation, name, label }` for a governing body row; label is "ABBR Name". */
export const mapGoverningBodyOption = (row) => {
  const id = Number(row?.id);
  if (!Number.isFinite(id)) return null;

  const abbreviation = toNull(row?.abbreviation) || toNull(row?.shortName) || "";
  const name = toNull(row?.name) || "";
  const searchLabel = [abbreviation, name].filter(Boolean).join(" ");
  const optionLabel = abbreviation && name ? `${abbreviation} ${name}` : searchLabel || `ID ${id}`;

  return {
    id,
    abbreviation,
    name,
    label: optionLabel,
  };
};

const dedupeById = (items) => {
  const byId = new Map();
  (Array.isArray(items) ? items : []).forEach((item) => {
    const id = Number(item?.id);
    if (!Number.isFinite(id)) return;
    const normalized = mapGoverningBodyOption(item);
    if (!normalized) return;
    byId.set(id, normalized);
  });
  return Array.from(byId.values());
};

const GoverningBodiesMultiSelect = ({ field, label, size, initialOptions }) => {
  const classes = useStyles();
  const { value, onChange, name } = field;
  const safeInitialOptions = useMemo(
    () => dedupeById(initialOptions),
    [initialOptions],
  );
  const committedValue = useMemo(() => dedupeById(value), [value]);

  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [draftValue, setDraftValue] = useState(committedValue);
  const [options, setOptions] = useState(safeInitialOptions);
  const [loading, setLoading] = useState(false);

  const searchTimeoutRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (open) return;
    setDraftValue(committedValue);
  }, [committedValue, open]);

  const runSearch = async (rawQuery, withLoading = true) => {
    const query = String(rawQuery || "").trim();
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (withLoading) {
      setLoading(true);
    }

    try {
      const endpoint = query
        ? GET_GOVERNING_BODIES_ADMIN_SEARCH({
            key: query,
            page: 0,
            limit: 100,
            sort: "Name",
            isAscending: true,
          })
        : GET_GOVERNING_BODIES_ADMIN_ALL({
            page: 0,
            limit: 100,
            sort: "Name",
            isAscending: true,
          });
      const payload = await getList(endpoint);
      if (requestId !== requestIdRef.current) return;

      const normalized = normalizeGoverningBodiesPayload(payload, "en-US");
      const queriedOptions = dedupeById(
        (normalized?.rows || []).map((row) => ({
          id: row?.id,
          abbreviation: row?.abbreviation || row?.shortName,
          shortName: row?.shortName,
          name: row?.name,
        })),
      );

      const pinnedSelected = dedupeById(draftValue);
      const next = dedupeById([...pinnedSelected, ...queriedOptions]);
      setOptions(next);
    } catch (_) {
      if (requestId !== requestIdRef.current) return;
      setOptions(dedupeById(draftValue));
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setDraftValue(committedValue);
    runSearch(searchQuery, true);
  };

  const handleClose = () => {
    requestIdRef.current += 1;
    clearTimeout(searchTimeoutRef.current);
    setOpen(false);
    setSearchQuery("");
    setDraftValue(committedValue);
  };

  const handleApply = () => {
    requestIdRef.current += 1;
    clearTimeout(searchTimeoutRef.current);
    onChange(dedupeById(draftValue));
    setOpen(false);
    setSearchQuery("");
  };

  const handleCancel = () => {
    requestIdRef.current += 1;
    clearTimeout(searchTimeoutRef.current);
    setOpen(false);
    setSearchQuery("");
    setDraftValue(committedValue);
  };

  const handleInputChange = (_, inputValue, reason) => {
    if (!open) return;
    const query = String(inputValue || "");
    setSearchQuery(query);

    if (reason !== "input" && reason !== "clear") {
      return;
    }

    clearTimeout(searchTimeoutRef.current);
    const trimmed = query.trim();

    if (trimmed.length > 0 && trimmed.length < MIN_SEARCH_KEY_LENGTH) {
      setOptions(dedupeById(draftValue));
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      runSearch(trimmed, false);
    }, TIME_OUT_REQUEST);
  };

  useEffect(
    () => () => {
      requestIdRef.current += 1;
      clearTimeout(searchTimeoutRef.current);
    },
    [],
  );

  const resolvedValue = open ? draftValue : committedValue;
  const handleSelectionChange = (_, nextValue) => {
    const normalized = dedupeById(nextValue);
    if (open) {
      setDraftValue(normalized);
      return;
    }
    onChange(normalized);
  };

  return (
    <Autocomplete
      id={name}
      multiple
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      options={options}
      value={resolvedValue}
      inputValue={searchQuery}
      onInputChange={handleInputChange}
      onChange={handleSelectionChange}
      loading={loading}
      disableCloseOnSelect
      filterOptions={(opts) => opts}
      getOptionLabel={(option) => option?.label || ""}
      getOptionSelected={(option, selected) => option?.id === selected?.id}
      ListboxProps={{ className: classes.listbox }}
      renderTags={(selected, getTagProps) =>
        selected.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            size="small"
            className={classes.chip}
            label={option?.abbreviation || option?.label || ""}
          />
        ))
      }
      renderOption={(option, { selected }) => (
        <React.Fragment>
          <Checkbox
            style={{ marginRight: 8 }}
            checked={selected}
            color="primary"
          />
          <Box className={classes.optionLabel}>{option?.label || ""}</Box>
        </React.Fragment>
      )}
      PaperComponent={(paperProps) => (
        <Paper
          {...paperProps}
          className={[paperProps.className, classes.paper].filter(Boolean).join(" ")}
        >
          {paperProps.children}
          <Box className={classes.footer}>
            <Button
              size="small"
              onMouseDown={(event) => event.preventDefault()}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              size="small"
              color="primary"
              variant="contained"
              onMouseDown={(event) => event.preventDefault()}
              onClick={handleApply}
            >
              Apply
            </Button>
          </Box>
        </Paper>
      )}
      size={size}
      fullWidth
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={label}
          placeholder={open ? "Type to search" : ""}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};

GoverningBodiesMultiSelect.propTypes = {
  field: PropTypes.object.isRequired,
  initialOptions: PropTypes.array,
  label: PropTypes.string,
  size: PropTypes.string,
};

GoverningBodiesMultiSelect.defaultProps = {
  initialOptions: [],
  label: "",
  size: "small",
};

export default GoverningBodiesMultiSelect;
